import { useEffect, useRef, useState, type MutableRefObject, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  BarChart2,
  Check,
  CheckCircle,
  ChevronRight,
  ClipboardList,
  Clock,
  Lock,
  Plus,
  Printer,
  RotateCcw,
  ScanLine,
  X,
} from 'lucide-react';
import { Badge } from '../../components/figma-ui';
import { PosBackButton } from '../../components/PosBackButton';
import { useStockCountViewModel } from './StockCountViewModel';
import {
  countModeLabel,
  countTypeLabel,
  findActiveSession,
  formatSessionTime,
  isHighVariance,
  primaryVariance,
} from './stockCountTypes';
import type { CountMode, CountType, LineStatus } from './stockCountTypes';
import {
  CrossAppBanner,
  InvStatusBadge,
  ProductThumb,
  ProgressRingLabel,
  QuantityKeypad,
  StepBarDark,
  StockCountAccessDenied,
  VBadge,
} from './StockCountShared';

interface PosStockCountScreenProps {
  onBack: () => void;
  userRole: string;
  branchName?: string;
}

export function PosStockCountScreen({
  onBack,
  userRole,
  branchName,
}: PosStockCountScreenProps) {
  const { t } = useTranslation();
  const canAccess = userRole === 'manager' || userRole === 'admin';
  const { state, intents } = useStockCountViewModel();
  const localScanRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (state.view === 'count' && localScanRef.current) {
      localScanRef.current.focus();
      intents.registerScanRef(localScanRef.current);
    }
  }, [state.view, state.activeLineId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canAccess) {
    return <StockCountAccessDenied onBack={onBack} />;
  }

  const active = state.activeSession ?? findActiveSession(state.sessions);
  const headerTitle =
    state.view === 'list'
      ? t('pos.stockcount.title')
      : state.view === 'wizard'
        ? t('pos.stockcount.new_count')
        : state.view === 'count'
          ? `${t('pos.stockcount.counting')} — ${state.selected?.visibleId ?? ''}`
          : state.view === 'review'
            ? t('pos.stockcount.review_title')
            : t('pos.stockcount.report_title');

  const handleTopBack = () => {
    if (state.view === 'list') onBack();
    else if (state.view === 'wizard') intents.goToList();
    else if (state.view === 'count') intents.goToList();
    else if (state.view === 'review') intents.setView('count');
    else intents.goToList();
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-brand-dark relative min-h-0">
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3 shrink-0">
        <PosBackButton onClick={handleTopBack} />
        <ClipboardList size={14} className="text-brand" />
        <span className="text-white font-bold text-sm">{headerTitle}</span>
        {active && state.view === 'list' && (
          <span className="ml-auto text-xs text-brand font-medium tabular-nums">
            {t('pos.stockcount.active_banner', {
              id: active.visibleId,
              percent: Math.round(active.progress.percent),
            })}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-hidden min-h-0">
        {state.view === 'list' && (
          <ListView
            state={state}
            intents={intents}
            branchName={branchName}
            t={t}
          />
        )}
        {state.view === 'wizard' && <WizardView state={state} intents={intents} t={t} />}
        {state.view === 'count' && state.selected && (
          <CountView
            state={state}
            intents={intents}
            scanRef={localScanRef}
            t={t}
          />
        )}
        {state.view === 'review' && state.selected && (
          <ReviewView state={state} intents={intents} t={t} />
        )}
        {state.view === 'report' && state.selected && (
          <ReportView state={state} t={t} />
        )}
      </div>

      {state.showApproveModal && state.selected && (
        <ApproveModal state={state} intents={intents} t={t} />
      )}

      {state.showForceSubmit && (
        <ForceSubmitModal state={state} intents={intents} t={t} />
      )}

      {state.notification.show && (
        <div
          className={`fixed top-20 right-4 text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-md ${
            state.notification.type === 'success'
              ? 'bg-green-500'
              : state.notification.type === 'error'
                ? 'bg-red-500'
                : 'bg-blue-500'
          }`}
        >
          <div className="flex items-center gap-2">
            {state.notification.type === 'success' && <Check className="w-5 h-5" />}
            {state.notification.type === 'error' && <X className="w-5 h-5" />}
            <span className="font-medium">{state.notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── List ─────────────────────────────────────────────────────────────────── */

function ListView({
  state,
  intents,
  branchName,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  intents: ReturnType<typeof useStockCountViewModel>['intents'];
  branchName?: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const active = state.activeSession ?? findActiveSession(state.sessions);

  return (
    <div className="flex flex-col h-full bg-brand-dark">
      <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between shrink-0">
        <div>
          <div className="text-white font-bold text-base">{t('pos.stockcount.list_title')}</div>
          <div className="text-gray-500 text-xs">{branchName}</div>
        </div>
        <button
          type="button"
          onClick={intents.startWizard}
          disabled={!!active}
          className="flex items-center gap-1.5 px-4 py-2.5 bg-brand text-white text-sm font-bold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed min-h-[48px]"
        >
          <Plus size={14} />
          {t('pos.stockcount.new_count')}
        </button>
      </div>

      {active && (
        <div className="mx-4 mt-4 flex items-center gap-3 bg-brand/20 border border-brand/30 rounded-xl px-4 py-3">
          <div className="w-2 h-2 rounded-full bg-brand animate-pulse shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-brand text-xs font-bold">
              {active.visibleId} — {t('pos.stockcount.status.in_progress')}
            </div>
            <div className="text-gray-600 text-[10px]">
              {formatSessionTime(active.startedAt)} · {Math.round(active.progress.percent)}%
            </div>
          </div>
          {state.confirmCancelId === active.visibleId ? (
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-red-400 font-medium">{t('pos.stockcount.cancel_confirm')}</span>
              <button
                type="button"
                onClick={() => void intents.cancelSession(active.visibleId)}
                className="px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded-lg min-h-[40px]"
              >
                {t('pos.stockcount.yes')}
              </button>
              <button
                type="button"
                onClick={() => intents.setConfirmCancelId(null)}
                className="px-3 py-1.5 bg-gray-700 text-gray-300 text-xs rounded-lg min-h-[40px]"
              >
                {t('pos.stockcount.no')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => intents.setConfirmCancelId(active.visibleId)}
                className="px-3 py-1.5 bg-red-900/40 border border-red-700/50 text-red-400 text-xs font-semibold rounded-lg flex items-center gap-1 min-h-[40px]"
              >
                <X size={11} />
                {t('pos.stockcount.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void intents.openSession(active)}
                className="px-3 py-1.5 bg-brand text-white text-xs font-bold rounded-lg min-h-[40px]"
              >
                {t('pos.stockcount.continue')}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {state.isLoading && state.sessions.length === 0 && (
          <div className="text-center text-gray-500 text-sm py-16">{t('pos.stockcount.loading')}</div>
        )}
        {!state.isLoading && state.sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <ClipboardList size={32} className="text-gray-700" />
            <div className="text-sm text-gray-500">{t('pos.stockcount.empty')}</div>
          </div>
        )}
        {state.sessions.map((s) => (
          <button
            key={s.visibleId}
            type="button"
            onClick={() => void intents.openSession(s)}
            className="w-full text-left bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-brand/50 transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-brand">{s.visibleId}</span>
                  <InvStatusBadge status={s.status} />
                  <Badge variant="outline">{countTypeLabel(s.type)}</Badge>
                </div>
                <div className="text-gray-500 text-[10px] mt-1.5">
                  {t('pos.stockcount.created_by', {
                    name: s.createdByName,
                    date: formatSessionTime(s.createdAt),
                  })}
                </div>
                {s.notes && (
                  <div className="text-gray-600 text-[10px] mt-1 truncate">{s.notes}</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <ProgressRingLabel percent={s.progress.percent} size={36} />
                <ChevronRight size={14} className="text-gray-700" />
              </div>
            </div>
            {s.status === 'closed' &&
              (s.variance.shortageLines !== 0 ||
                s.variance.surplusLines !== 0 ||
                s.variance.netUnits !== 0) && (
              <div className="mt-3 flex gap-3 text-[10px]">
                <span className="text-red-400">
                  {t('pos.stockcount.shortages')}: {s.variance.shortageLines}
                </span>
                <span className="text-green-400">
                  {t('pos.stockcount.surpluses')}: {s.variance.surplusLines}
                </span>
                <span
                  className={
                    s.variance.netUnits < 0 ? 'text-red-400' : 'text-green-400'
                  }
                >
                  {t('pos.stockcount.net_variance')}:{' '}
                  {s.variance.netUnits > 0 ? '+' : ''}
                  {s.variance.netUnits}
                </span>
              </div>
            )}
          </button>
        ))}
        {(state.hasMoreSessions || state.isLoading) && state.sessions.length > 0 && (
          <SessionListSentinel
            enabled={state.hasMoreSessions && !state.isLoading}
            onLoadMore={() => void intents.loadMoreSessions()}
            loading={state.isLoading}
          />
        )}
      </div>
    </div>
  );
}

function SessionListSentinel({
  enabled,
  onLoadMore,
  loading,
}: {
  enabled: boolean;
  onLoadMore: () => void;
  loading: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!enabled) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, onLoadMore]);
  return (
    <div ref={ref} className="text-center text-gray-500 text-xs py-3">
      {loading ? '…' : ''}
    </div>
  );
}

/* ─── Wizard ───────────────────────────────────────────────────────────────── */

const WIZARD_STEPS = ['Type', 'Périmètre', 'Mode', 'Seuils', 'Notes'];

/** Scan-first product selection for `spot` counts. */
function ProductPicker({
  state,
  intents,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  intents: ReturnType<typeof useStockCountViewModel>['intents'];
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selected = state.wizard.products;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500">{t('pos.stockcount.perimeter_spot_hint')}</div>

      <div
        className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-xl px-3 py-2"
        onClick={() => inputRef.current?.focus()}
      >
        <ScanLine size={14} className="text-brand shrink-0" />
        <input
          ref={(el) => {
            inputRef.current = el;
            intents.registerProductScanRef(el);
          }}
          value={state.productSearch}
          onChange={(e) => intents.setProductSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void intents.submitProductScan();
            }
          }}
          placeholder={t('pos.stockcount.product_search_placeholder')}
          className="bg-transparent flex-1 text-sm text-white placeholder-gray-600 outline-none min-h-[40px]"
        />
      </div>

      {state.productSearching && (
        <div className="text-xs text-gray-500">{t('pos.stockcount.product_searching')}</div>
      )}

      {!state.productSearching && state.productResults.length > 0 && (
        <div className="rounded-xl border border-gray-800 overflow-hidden divide-y divide-gray-800">
          {state.productResults.map((p) => {
            const already = selected.some((s) => s.id === p.id);
            return (
              <button
                key={p.id}
                type="button"
                disabled={already}
                onClick={() => intents.addWizardProduct(p)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 min-h-[48px] transition-colors ${
                  already ? 'bg-gray-900/40 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.name}</div>
                  <div className="text-[10px] text-gray-500 font-mono">
                    {p.sku}
                    {p.barcode ? ` · ${p.barcode}` : ''}
                  </div>
                </div>
                {already ? (
                  <Check size={14} className="text-brand shrink-0" />
                ) : (
                  <Plus size={14} className="text-gray-500 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {!state.productSearching &&
        state.productSearch.trim().length >= 2 &&
        state.productResults.length === 0 && (
          <div className="text-xs text-gray-500">{t('pos.stockcount.product_no_results')}</div>
        )}

      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {t('pos.stockcount.product_selected_count', { n: selected.length })}
      </div>

      {selected.length > 0 && (
        <div className="space-y-2">
          {selected.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{p.name}</div>
                <div className="text-[10px] text-gray-500 font-mono">{p.sku}</div>
              </div>
              <button
                type="button"
                onClick={() => intents.removeWizardProduct(p.id)}
                aria-label={t('pos.stockcount.product_remove')}
                className="p-2 text-gray-500 hover:text-red-400 rounded-lg min-h-[48px] min-w-[48px] flex items-center justify-center"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WizardView({
  state,
  intents,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  intents: ReturnType<typeof useStockCountViewModel>['intents'];
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const { wizard, wizardStep } = state;
  const cardBg = 'bg-gray-900 border-gray-800';
  const inputCls =
    'px-3 py-2 text-sm rounded-xl outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand border bg-gray-800 border-gray-700 text-white placeholder-gray-600 w-full';

  if (wizardStep === 'freeze') {
    const sess = state.draftSession ?? state.selected;
    return (
      <div className="flex flex-col h-full items-center justify-center gap-5 bg-brand-dark p-6">
        <div className="w-14 h-14 bg-brand/20 rounded-2xl flex items-center justify-center">
          <Lock size={24} className="text-brand" />
        </div>
        <div className="text-center max-w-sm">
          <div className="font-bold text-lg mb-2 text-white">{t('pos.stockcount.freeze_title')}</div>
          <div className="text-sm leading-relaxed text-gray-500">
            {t('pos.stockcount.freeze_hint')}
          </div>
        </div>
        {sess && (
          <div className={`w-full max-w-xs rounded-xl border p-4 space-y-2 text-xs ${cardBg}`}>
            <div className="flex justify-between">
              <span className="text-gray-500">Session</span>
              <span className="font-mono font-bold text-white">{sess.visibleId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Type</span>
              <span className="text-white">{countTypeLabel(sess.type)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Mode</span>
              <span className="text-white">{countModeLabel(sess.countMode)}</span>
            </div>
          </div>
        )}
        <button
          type="button"
          disabled={state.isSaving}
          onClick={() => void intents.startCounting()}
          className="px-8 py-3 bg-brand text-white font-bold rounded-xl text-sm flex items-center gap-2 min-h-[48px] disabled:opacity-50"
        >
          <ScanLine size={14} />
          {t('pos.stockcount.start_counting')}
        </button>
      </div>
    );
  }

  const stepNum = wizardStep as number;

  return (
    <div className="flex flex-col h-full bg-brand-dark">
      <div className="px-6 py-4 shrink-0">
        <StepBarDark steps={WIZARD_STEPS} current={stepNum} />
      </div>
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {stepNum === 1 && (
          <div className="space-y-3 max-w-lg">
            <div className="text-xs font-semibold uppercase tracking-wide mb-3 text-gray-500">
              {t('pos.stockcount.wizard_type')}
            </div>
            {(
              [
                ['full', 'Complet', t('pos.stockcount.type_full_hint')],
                ['cycle', 'Cyclique', t('pos.stockcount.type_cycle_hint')],
                ['spot', 'Ponctuel', t('pos.stockcount.type_spot_hint')],
              ] as [CountType, string, string][]
            ).map(([id, label, hint]) => (
              <label
                key={id}
                className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                  wizard.type === id
                    ? 'border-brand bg-brand/10'
                    : `${cardBg} border`
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  checked={wizard.type === id}
                  onChange={() => intents.setWizardType(id)}
                  className="mt-0.5 accent-brand"
                />
                <div>
                  <div className="font-semibold text-sm text-white">{label}</div>
                  <div className="text-xs mt-0.5 text-gray-500">{hint}</div>
                </div>
              </label>
            ))}
          </div>
        )}
        {stepNum === 2 && (
          <div className="space-y-4 max-w-lg">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1 text-gray-500">
              {wizard.type === 'spot'
                ? t('pos.stockcount.wizard_products')
                : t('pos.stockcount.wizard_perimeter')}
            </div>
            {wizard.type === 'full' && (
              <div className="text-sm text-gray-400">{t('pos.stockcount.perimeter_full')}</div>
            )}
            {wizard.type === 'cycle' && (
              <div className="flex flex-wrap gap-2">
                {state.categories.map((c) => {
                  const on = wizard.categoryIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        intents.patchWizard({
                          categoryIds: on
                            ? wizard.categoryIds.filter((x) => x !== c.id)
                            : [...wizard.categoryIds, c.id],
                        })
                      }
                      className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-colors min-h-[48px] ${
                        on
                          ? 'bg-brand text-white border-brand'
                          : `${cardBg} text-gray-500`
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
                {state.categories.length === 0 && (
                  <div className="text-sm text-gray-500">{t('pos.stockcount.no_categories')}</div>
                )}
              </div>
            )}
            {wizard.type === 'spot' && (
              <ProductPicker state={state} intents={intents} t={t} />
            )}
            {state.wizardError && (
              <div className="flex items-center gap-1.5 text-xs text-red-400">
                <AlertTriangle size={12} />
                {state.wizardError}
              </div>
            )}
          </div>
        )}
        {stepNum === 3 && (
          <div className="space-y-3 max-w-lg">
            <div className="text-xs font-semibold uppercase tracking-wide mb-3 text-gray-500">
              {t('pos.stockcount.wizard_mode')}
            </div>
            {(
              [
                ['total_only', 'Total seul', t('pos.stockcount.mode_total_hint')],
                ['dual', 'Stock total + Exposé', t('pos.stockcount.mode_dual_hint')],
                ['floor_only', 'Exposé seul', t('pos.stockcount.mode_floor_hint')],
              ] as [CountMode, string, string][]
            ).map(([id, label, hint]) => (
              <label
                key={id}
                className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer mb-2 transition-colors ${
                  wizard.countMode === id
                    ? 'border-brand bg-brand/10'
                    : `${cardBg} border`
                }`}
              >
                <input
                  type="radio"
                  name="mode"
                  checked={wizard.countMode === id}
                  onChange={() => intents.setWizardMode(id)}
                  className="mt-0.5 accent-brand"
                />
                <div>
                  <div className="font-semibold text-sm text-white">{label}</div>
                  <div className="text-xs mt-0.5 text-gray-500">{hint}</div>
                </div>
              </label>
            ))}
          </div>
        )}
        {stepNum === 4 && (
          <div className="max-w-lg">
            <div className="text-xs font-semibold uppercase tracking-wide mb-3 text-gray-500">
              {t('pos.stockcount.wizard_thresholds')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-gray-500">
                  {t('pos.stockcount.threshold_units')}
                </label>
                <input
                  type="number"
                  min={0}
                  value={wizard.thresholdUnits}
                  onChange={(e) =>
                    intents.patchWizard({ thresholdUnits: Number(e.target.value) || 0 })
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-gray-500">
                  {t('pos.stockcount.threshold_percent')}
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  value={wizard.thresholdPercent}
                  onChange={(e) =>
                    intents.patchWizard({
                      thresholdPercent: Number(e.target.value) || 0,
                    })
                  }
                  className={inputCls}
                />
              </div>
            </div>
          </div>
        )}
        {stepNum === 5 && (
          <div className="space-y-4 max-w-lg">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5 text-gray-500">
                {t('pos.stockcount.wizard_notes')}
              </label>
              <textarea
                value={wizard.notes}
                onChange={(e) => intents.patchWizard({ notes: e.target.value })}
                rows={3}
                placeholder={t('pos.stockcount.notes_placeholder')}
                className={`${inputCls} resize-none`}
              />
            </div>
            <div className={`rounded-xl border p-4 space-y-2 text-xs ${cardBg}`}>
              <div className="font-semibold text-sm mb-2 text-white">
                {t('pos.stockcount.recap')}
              </div>
              {(
                [
                  ['Type', countTypeLabel(wizard.type)],
                  ['Mode', countModeLabel(wizard.countMode)],
                  wizard.type === 'spot'
                    ? [
                        t('pos.stockcount.recap_products'),
                        `${wizard.products.length}`,
                      ]
                    : [
                        'Catégories',
                        wizard.type === 'full'
                          ? t('pos.stockcount.all_categories')
                          : `${wizard.categoryIds.length}`,
                      ],
                  ['Seuil unités', `≥ ${wizard.thresholdUnits}`],
                  ['Seuil %', `≥ ${wizard.thresholdPercent}%`],
                ] as [string, string][]
              ).map(([l, v]) => (
                <div key={l} className="flex justify-between gap-4">
                  <span className="text-gray-500">{l}</span>
                  <span className="font-medium text-right text-white">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-gray-800 flex justify-between shrink-0">
        <button
          type="button"
          onClick={() =>
            stepNum > 1
              ? intents.setWizardStep((stepNum - 1) as 1 | 2 | 3 | 4 | 5)
              : intents.goToList()
          }
          className="px-4 py-2.5 border border-gray-700 text-gray-300 rounded-xl text-sm min-h-[48px]"
        >
          {stepNum === 1 ? t('pos.stockcount.cancel') : t('pos.stockcount.prev')}
        </button>
        {stepNum < 5 ? (
          <button
            type="button"
            onClick={() => intents.goToNextWizardStep()}
            className="px-4 py-2.5 bg-brand text-white rounded-xl text-sm font-bold min-h-[48px]"
          >
            {t('pos.stockcount.next')}
          </button>
        ) : (
          <button
            type="button"
            disabled={state.isSaving}
            onClick={() => void intents.createDraft()}
            className="px-4 py-2.5 bg-brand text-white rounded-xl text-sm font-bold min-h-[48px] disabled:opacity-50"
          >
            {t('pos.stockcount.create_draft')}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Count station ────────────────────────────────────────────────────────── */

function CountView({
  state,
  intents,
  scanRef,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  intents: ReturnType<typeof useStockCountViewModel>['intents'];
  scanRef: RefObject<HTMLInputElement | null>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const session = state.selected!;
  const activeLine = state.lines.find((l) => l.id === state.activeLineId) ?? null;
  const progress = session.progress;

  return (
    <div className="flex flex-col h-full bg-brand-dark">
      <CrossAppBanner id={session.visibleId} />
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3 shrink-0">
        <div
          className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-xl flex-1 px-3 py-2"
          onClick={() => scanRef.current?.focus()}
        >
          <ScanLine size={14} className="text-brand shrink-0" />
          <input
            ref={(el) => {
              (scanRef as MutableRefObject<HTMLInputElement | null>).current = el;
              intents.registerScanRef(el);
            }}
            value={state.scanValue}
            onChange={(e) => intents.setScanValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                intents.selectLineByScan();
              }
            }}
            placeholder={t('pos.stockcount.scan_placeholder')}
            className="bg-transparent flex-1 text-sm text-white placeholder-gray-600 outline-none"
            autoFocus
          />
        </div>
        <ProgressRingLabel percent={progress.percent} size={40} />
        <button
          type="button"
          onClick={() => void intents.submitForReview(false)}
          className="px-4 py-2 bg-brand text-white text-xs font-bold rounded-xl flex items-center gap-1.5 min-h-[48px]"
        >
          <BarChart2 size={12} />
          {t('pos.stockcount.submit_review')}
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        <div className="flex-1 flex flex-col p-4 gap-4 overflow-y-auto">
          {activeLine ? (
            <>
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 flex gap-4">
                <ProductThumb src={activeLine.imageUrl} alt={activeLine.productName} size="lg" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-base">{activeLine.productName}</div>
                  <div className="text-gray-500 text-xs font-mono mt-0.5">
                    {activeLine.sku}
                    {activeLine.barcode ? ` · ${activeLine.barcode}` : ''}
                  </div>
                  {(activeLine.countedByName || activeLine.source) && (
                    <div className="text-[10px] text-gray-500 mt-1">
                      {activeLine.countedByName && (
                        <span>
                          {t('pos.stockcount.counted_by', { name: activeLine.countedByName })}
                        </span>
                      )}
                      {activeLine.source && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 font-semibold uppercase">
                          {activeLine.source}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-6 mt-3">
                    <div>
                      <div className="text-[10px] text-gray-600 uppercase tracking-wide">
                        {t('pos.stockcount.theoretical_stock')}
                      </div>
                      <div className="text-2xl font-bold tabular-nums text-gray-400 mt-0.5">
                        {activeLine.theoreticalStock}
                      </div>
                    </div>
                    {(session.countMode === 'dual' || session.countMode === 'floor_only') && (
                      <div>
                        <div className="text-[10px] text-gray-600 uppercase tracking-wide">
                          {t('pos.stockcount.theoretical_exposed')}
                        </div>
                        <div className="text-2xl font-bold tabular-nums text-gray-400 mt-0.5">
                          {activeLine.theoreticalExposed}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {session.countMode === 'dual' && (
                <div className="flex gap-2">
                  {(['stock', 'exposed'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => intents.setKeyMode(m)}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-colors min-h-[48px] ${
                        state.keyMode === m
                          ? 'bg-brand border-brand text-white'
                          : 'bg-gray-900 border-gray-800 text-gray-500'
                      }`}
                    >
                      {m === 'stock'
                        ? t('pos.stockcount.counted_total')
                        : t('pos.stockcount.counted_exposed')}
                    </button>
                  ))}
                </div>
              )}

              <div className="bg-gray-900 border border-gray-700 rounded-2xl p-4 text-center">
                <div className="text-[10px] text-gray-600 uppercase tracking-wide mb-2">
                  {state.keyMode === 'stock' || session.countMode === 'total_only'
                    ? t('pos.stockcount.enter_physical_stock')
                    : t('pos.stockcount.enter_physical_exposed')}
                </div>
                <div className="text-5xl font-bold tabular-nums text-white min-h-[60px] flex items-center justify-center">
                  {state.keyInput || <span className="text-gray-700">—</span>}
                </div>
                {state.lineError && (
                  <div className="mt-2 text-xs text-red-400 flex items-center justify-center gap-1">
                    <AlertTriangle size={12} />
                    {state.lineError}
                  </div>
                )}
              </div>

              <QuantityKeypad onKey={(k) => void intents.keyPress(k)} />
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center">
              <ScanLine size={40} className="text-gray-700" />
              <div className="text-gray-600 text-sm">{t('pos.stockcount.scan_to_start')}</div>
              {progress.counted === progress.total && progress.total > 0 && (
                <button
                  type="button"
                  onClick={() => void intents.submitForReview(false)}
                  className="mt-3 px-6 py-3 bg-brand text-white text-sm font-bold rounded-xl flex items-center gap-2 min-h-[48px]"
                >
                  <BarChart2 size={14} />
                  {t('pos.stockcount.all_counted')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="w-56 border-l border-gray-800 flex flex-col overflow-hidden shrink-0">
          <div className="px-3 py-2 border-b border-gray-800 text-[10px] text-gray-600 font-bold uppercase tracking-wide shrink-0">
            {t('pos.stockcount.queue')}
          </div>
          <div className="flex-1 overflow-y-auto">
            {(['recount', 'pending', 'counted'] as LineStatus[]).map((status) => {
              const group = state.lines.filter((l) => l.status === status);
              if (!group.length) return null;
              const label =
                status === 'counted'
                  ? t('pos.stockcount.queue_counted')
                  : status === 'recount'
                    ? t('pos.stockcount.queue_recount')
                    : t('pos.stockcount.queue_pending');
              const col =
                status === 'counted'
                  ? 'text-green-500'
                  : status === 'recount'
                    ? 'text-amber-500'
                    : 'text-gray-600';
              return (
                <div key={status}>
                  <div className={`px-3 py-1.5 text-[9px] font-bold uppercase tracking-wide ${col}`}>
                    {label} ({group.length})
                  </div>
                  {group.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => intents.setActiveLineId(l.id)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                        state.activeLineId === l.id
                          ? 'bg-brand/20 border-l-2 border-brand'
                          : 'hover:bg-gray-900'
                      }`}
                    >
                      <ProductThumb src={l.imageUrl} alt={l.productName} size="sm" />
                      <div className="min-w-0">
                        <div className="text-[10px] text-white truncate">{l.productName}</div>
                        <div className="text-[9px] text-gray-600 tabular-nums">
                          {l.status === 'counted' ? `C: ${l.countedStock}` : ''}
                          {l.source ? ` · ${l.source.toUpperCase()}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
            {state.linesLoading && (
              <div className="p-4 text-xs text-gray-600">{t('pos.stockcount.loading')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Review ───────────────────────────────────────────────────────────────── */

function ReviewView({
  state,
  intents,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  intents: ReturnType<typeof useStockCountViewModel>['intents'];
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const session = state.selected!;
  const v = session.variance;
  const [filter, setFilter] = useState<
    'all' | 'manques' | 'surplus' | 'élevé' | 'non_compté'
  >('all');

  const filtered = state.lines.filter((line) => {
    const vs = primaryVariance(line, session.countMode);
    if (filter === 'manques') return vs !== null && vs < 0;
    if (filter === 'surplus') return vs !== null && vs > 0;
    if (filter === 'élevé')
      return isHighVariance(
        line,
        session.countMode,
        session.thresholdUnits,
        session.thresholdPercent,
      );
    if (filter === 'non_compté')
      return line.status === 'pending' || line.status === 'recount';
    return true;
  });

  const filters: [typeof filter, string, string][] = [
    ['all', t('pos.stockcount.filter_all'), String(state.lines.length)],
    ['manques', t('pos.stockcount.shortages'), String(v.shortageLines)],
    ['surplus', t('pos.stockcount.surpluses'), String(v.surplusLines)],
    ['élevé', t('pos.stockcount.high_variance'), String(v.highVarianceLines)],
    [
      'non_compté',
      t('pos.stockcount.uncounted'),
      String(session.progress.pending + session.progress.recount),
    ],
  ];

  return (
    <div className="flex flex-col h-full bg-brand-dark">
      <CrossAppBanner id={session.visibleId} />
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3 shrink-0">
        <div className="flex-1">
          <div className="text-white font-bold text-sm">{t('pos.stockcount.review_title')}</div>
          <div className="text-gray-500 text-xs">
            {session.visibleId} · {session.progress.counted}/{session.progress.total}{' '}
            {t('pos.stockcount.counted_label')}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void intents.loadActivity()}
          className="px-3 py-2 bg-gray-800 text-gray-300 text-xs font-semibold rounded-xl flex items-center gap-1 min-h-[48px]"
        >
          <Clock size={11} />
          {t('pos.stockcount.activity')}
        </button>
        <button
          type="button"
          onClick={() => void intents.reopenCounting()}
          className="px-3 py-2 bg-amber-900/40 border border-amber-700/50 text-amber-400 text-xs font-semibold rounded-xl flex items-center gap-1 min-h-[48px]"
        >
          <RotateCcw size={11} />
          {t('pos.stockcount.reopen')}
        </button>
        <button
          type="button"
          onClick={() => intents.setShowApproveModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand text-white text-xs font-bold rounded-xl min-h-[48px]"
        >
          <CheckCircle size={13} />
          {t('pos.stockcount.approve')}
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2 px-4 py-3 border-b border-gray-800 shrink-0">
        {(
          [
            [t('pos.stockcount.shortages'), v.shortageLines, 'text-red-400'],
            [t('pos.stockcount.surpluses'), v.surplusLines, 'text-green-400'],
            [
              t('pos.stockcount.net_variance'),
              (v.netUnits > 0 ? '+' : '') + v.netUnits,
              v.netUnits < 0 ? 'text-red-400' : 'text-green-400',
            ],
            [t('pos.stockcount.high_variance'), v.highVarianceLines, 'text-amber-400'],
          ] as [string, string | number, string][]
        ).map(([l, val, c]) => (
          <div key={l} className="bg-gray-800/60 rounded-xl p-2.5 text-center">
            <div className={`text-base font-bold tabular-nums ${c}`}>{val}</div>
            <div className="text-[9px] text-gray-600 mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 px-4 py-2 overflow-x-auto shrink-0">
        {filters.map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            className={`flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors min-h-[36px] ${
              filter === id ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400'
            }`}
          >
            {label}{' '}
            <span className={filter === id ? 'opacity-70' : 'opacity-50'}>({count})</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
        {filtered.map((line) => {
          const high = isHighVariance(
            line,
            session.countMode,
            session.thresholdUnits,
            session.thresholdPercent,
          );
          return (
            <div
              key={line.id}
              className={`bg-gray-900 border rounded-xl p-3 flex items-center gap-3 ${
                high ? 'border-red-800/50' : 'border-gray-800'
              }`}
            >
              <ProductThumb src={line.imageUrl} alt={line.productName} />
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-semibold truncate">
                  {line.productName}
                </div>
                <div className="text-gray-600 text-[10px] font-mono mt-0.5">{line.sku}</div>
                <div className="flex gap-3 mt-1 text-[10px] text-gray-500">
                  <span>
                    Th:{' '}
                    <b className="text-gray-400 tabular-nums">{line.theoreticalStock}</b>
                  </span>
                  <span>
                    C:{' '}
                    <b className="text-white tabular-nums">{line.countedStock ?? '—'}</b>
                  </span>
                  {line.source && (
                    <span className="uppercase font-semibold">{line.source}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <VBadge variance={primaryVariance(line, session.countMode)} high={high} />
                <button
                  type="button"
                  onClick={() => void intents.markRecount(line.id)}
                  className="text-[9px] text-amber-400 flex items-center gap-0.5"
                >
                  <RotateCcw size={8} />
                  {t('pos.stockcount.recount')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {state.showActivity && (
        <div className="absolute inset-y-0 right-0 w-72 bg-gray-950 border-l border-gray-800 flex flex-col z-20 shadow-2xl">
          <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2">
            <Clock size={12} className="text-brand" />
            <span className="text-xs font-bold text-gray-200 flex-1">
              {t('pos.stockcount.activity_title')}
            </span>
            <button
              type="button"
              onClick={() => intents.setShowActivity(false)}
              className="text-gray-500 hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {state.activity.length === 0 && (
              <div className="text-xs text-gray-600">{t('pos.stockcount.activity_empty')}</div>
            )}
            {state.activity.map((ev) => (
              <div key={ev.id} className="flex gap-2">
                <div
                  className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                    ev.movementType === 'SALE' ? 'bg-amber-400' : 'bg-teal-400'
                  }`}
                />
                <div>
                  <div className="text-[10px] font-semibold text-gray-300">
                    {ev.reason || ev.productId}
                  </div>
                  <div className="text-[9px] text-gray-500">
                    {ev.movementType} · {ev.quantity}u
                  </div>
                  <div className="text-[9px] text-gray-600 tabular-nums">
                    {formatSessionTime(ev.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Report ───────────────────────────────────────────────────────────────── */

function ReportView({
  state,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const session = state.selected!;
  const summary = state.report?.summary ?? {
    ...session.progress,
    ...session.variance,
  };
  const lines = state.report?.lines ?? state.lines;

  return (
    <div className="h-full overflow-y-auto bg-brand-dark p-4 space-y-4">
      <div className="flex items-center gap-3 p-4 bg-green-900/30 border border-green-800/40 rounded-xl">
        <CheckCircle size={20} className="text-green-400 shrink-0" />
        <div>
          <div className="text-white font-bold text-sm">{t('pos.stockcount.closed_title')}</div>
          <div className="text-gray-500 text-xs">{t('pos.stockcount.closed_hint')}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {(
          [
            [
              t('pos.stockcount.counted_label'),
              `${summary.counted}/${summary.total}`,
              'text-brand',
            ],
            [t('pos.stockcount.shortages'), String(summary.shortageLines), 'text-red-400'],
            [t('pos.stockcount.surpluses'), String(summary.surplusLines), 'text-green-400'],
            [
              t('pos.stockcount.net_variance'),
              (summary.netUnits > 0 ? '+' : '') + summary.netUnits,
              summary.netUnits < 0 ? 'text-red-400' : 'text-green-400',
            ],
          ] as [string, string, string][]
        ).map(([l, val, c]) => (
          <div
            key={l}
            className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-center"
          >
            <div className={`text-xl font-bold tabular-nums ${c}`}>{val}</div>
            <div className="text-[9px] text-gray-600 mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-2 text-xs text-gray-400">
        <div>
          {t('pos.stockcount.created_by', {
            name: session.createdByName,
            date: formatSessionTime(session.createdAt),
          })}
        </div>
        {session.approvedByName && (
          <div>
            {t('pos.stockcount.approved_by', {
              name: session.approvedByName,
              date: formatSessionTime(session.closedAt),
            })}
          </div>
        )}
        {session.approveComment && (
          <div className="text-gray-500 italic">&quot;{session.approveComment}&quot;</div>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800 text-xs font-semibold text-gray-300">
          {t('pos.stockcount.adjustments_detail')}
        </div>
        <div className="divide-y divide-gray-800 max-h-64 overflow-y-auto">
          {lines.map((line) => (
            <div key={line.id} className="px-4 py-2.5 flex items-center gap-3 text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-gray-200 truncate">{line.productName}</div>
                <div className="text-gray-600 font-mono">{line.sku}</div>
              </div>
              <span className="tabular-nums text-gray-500">{line.theoreticalStock}</span>
              <span className="text-gray-600">→</span>
              <span className="tabular-nums text-white font-bold">
                {line.countedStock ?? '—'}
              </span>
              <VBadge
                variance={primaryVariance(line, session.countMode)}
                high={isHighVariance(
                  line,
                  session.countMode,
                  session.thresholdUnits,
                  session.thresholdPercent,
                )}
              />
              {line.countedByName && (
                <span className="text-[9px] text-gray-600 shrink-0">{line.countedByName}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => window.print()}
        className="w-full py-3 bg-brand text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 min-h-[48px]"
      >
        <Printer size={14} />
        {t('pos.stockcount.print_report')}
      </button>
    </div>
  );
}

/* ─── Modals ───────────────────────────────────────────────────────────────── */

function ApproveModal({
  state,
  intents,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  intents: ReturnType<typeof useStockCountViewModel>['intents'];
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const session = state.selected!;
  const hasHigh = session.variance.highVarianceLines > 0;
  const canConfirm = !hasHigh || state.acknowledgeHighVariance;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 pt-6 pb-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand/20 rounded-xl flex items-center justify-center">
              <CheckCircle size={20} className="text-brand" />
            </div>
            <div>
              <div className="font-bold text-white">{t('pos.stockcount.approve_title')}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {t('pos.stockcount.approve_hint')}
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="bg-gray-950 rounded-xl p-3.5 grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-bold tabular-nums text-white">
                {session.progress.counted}
              </div>
              <div className="text-[10px] text-gray-500">{t('pos.stockcount.lines_counted')}</div>
            </div>
            <div>
              <div
                className={`text-lg font-bold tabular-nums ${
                  session.variance.netUnits < 0
                    ? 'text-red-400'
                    : session.variance.netUnits > 0
                      ? 'text-green-400'
                      : 'text-white'
                }`}
              >
                {session.variance.netUnits > 0 ? '+' : ''}
                {session.variance.netUnits}
              </div>
              <div className="text-[10px] text-gray-500">{t('pos.stockcount.net_units')}</div>
            </div>
            <div>
              <div
                className={`text-lg font-bold tabular-nums ${
                  hasHigh ? 'text-red-400' : 'text-white'
                }`}
              >
                {session.variance.highVarianceLines}
              </div>
              <div className="text-[10px] text-gray-500">{t('pos.stockcount.high_variance')}</div>
            </div>
          </div>
          {hasHigh && (
            <label className="flex items-start gap-3 p-3 bg-red-950/40 border border-red-800/40 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={state.acknowledgeHighVariance}
                onChange={(e) => intents.setAcknowledgeHighVariance(e.target.checked)}
                className="mt-0.5 accent-red-600"
              />
              <span className="text-xs text-red-400 font-medium">
                {t('pos.stockcount.ack_high', {
                  count: session.variance.highVarianceLines,
                })}
              </span>
            </label>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5">
              {t('pos.stockcount.approve_comment')}
            </label>
            <textarea
              value={state.approveComment}
              onChange={(e) => intents.setApproveComment(e.target.value)}
              rows={2}
              placeholder={t('pos.stockcount.approve_comment_ph')}
              className="w-full text-sm border border-gray-700 rounded-lg px-3 py-2 resize-none bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
            />
          </div>
          <div className="text-xs text-sky-400 bg-sky-950/40 rounded-lg px-3 py-2 font-medium">
            {t('pos.stockcount.approve_irreversible')}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-800 flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => intents.setShowApproveModal(false)}
            className="px-4 py-2 border border-gray-700 text-gray-300 rounded-xl text-sm min-h-[48px]"
          >
            {t('pos.stockcount.cancel')}
          </button>
          <button
            type="button"
            disabled={!canConfirm || state.isSaving}
            onClick={() => void intents.confirmApprove()}
            className="px-4 py-2 bg-brand text-white rounded-xl text-sm font-bold flex items-center gap-1.5 min-h-[48px] disabled:opacity-40"
          >
            <CheckCircle size={13} />
            {t('pos.stockcount.confirm_align')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ForceSubmitModal({
  state,
  intents,
  t,
}: {
  state: ReturnType<typeof useStockCountViewModel>['state'];
  intents: ReturnType<typeof useStockCountViewModel>['intents'];
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="text-amber-400" size={22} />
          <div className="font-bold text-white">{t('pos.stockcount.force_title')}</div>
        </div>
        <p className="text-sm text-gray-400">{t('pos.stockcount.force_hint')}</p>
        <textarea
          value={state.forceReason}
          onChange={(e) => intents.setForceReason(e.target.value)}
          rows={2}
          placeholder={t('pos.stockcount.force_reason_ph')}
          className="w-full text-sm border border-gray-700 rounded-lg px-3 py-2 resize-none bg-gray-800 text-white"
        />
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => intents.setShowForceSubmit(false)}
            className="px-4 py-2 border border-gray-700 text-gray-300 rounded-xl text-sm min-h-[48px]"
          >
            {t('pos.stockcount.cancel')}
          </button>
          <button
            type="button"
            disabled={!state.forceReason.trim() || state.isSaving}
            onClick={() => void intents.submitForReview(true)}
            className="px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-bold min-h-[48px] disabled:opacity-40"
          >
            {t('pos.stockcount.force_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
